/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { Log } from "../../logging";
import type {
  TPlaceholderSpec,
  TTivoPlusContext,
  TTivoPlusValues
} from "./TivoPlusPlaceholders";
import {
  PlaceholderKind,
  TIVOPLUS_BLANK,
  TIVO_PLUS_PLACEHOLDERS,
  XUMO_KVPS_PARAM,
  XUMO_MARKER_PARAM
} from "./TivoPlusPlaceholders";

/**
 * Matches a placeholder token.
 *
 * The run of `[A-Z0-9_]` is greedy, which is what makes a token that is a
 * strict prefix of another safe: `TIVOPLUS_GDPRCONSENT` is consumed whole,
 * so `TIVOPLUS_GDPR` can never absorb it. The reference client achieves the
 * same by ordering its replacements; matching the longest run makes the
 * guarantee structural instead of positional.
 */
const PLACEHOLDER_PATTERN: RegExp = /TIVOPLUS_[A-Z0-9_]+/g;

/**
 * Characters XUMO's server cannot accept inside parameter values that sit
 * outside the grouped block. Stripped rather than escaped, per the
 * specification.
 */
const XUMO_UNSAFE_CHARACTERS: RegExp = /[\]\s<>"#%{}|\\^~[';/?:@=&]/g;

/** Encoding depth for a value inside the grouped block. */
const KVPS_ENCODE_DEPTH: number = 3;

/** Encoding depth for a value anywhere else. */
const DEFAULT_ENCODE_DEPTH: number = 1;

/** Where in the URL a placeholder sits, which decides its encoding. */
type TResolveOptions = {
  isXumo: boolean;
  kvpsStart: number;
  kvpsEnd: number;
};

/**
 * Resolves the `TIVOPLUS_*` placeholders a TiVo+ playback URL arrives with.
 *
 * TiVo+ channels deliver a *template*, not a playable URL: ad parameters are
 * left as placeholders for the client to fill in with device, application
 * and privacy values. Leaving them unresolved is not a visible failure —
 * the stream still plays, while every impression goes out untargeted — so
 * substitution is part of the normal playback path, not a nicety.
 *
 * The URL is treated as opaque text throughout. Parameter *names* are never
 * inspected, so a content partner renaming one needs no change here, and
 * parameter order and casing survive untouched. That also means placeholders
 * sitting inside an already-encoded block resolve like any other.
 *
 * Port of `resolveTivoPlusUrlParams` / `replaceRuntimeUrlParams` /
 * `replaceUrlParam` from the reference client.
 */
export class TivoPlusUrlResolver {
  private static readonly TAG: string = "TivoPlusUrlResolver";

  /** Unknown tokens already reported, so each is logged once per session. */
  private static readonly oReportedUnknownTokens: Set<string> =
    new Set<string>();

  /** Placeholder lookup, built once from the catalogue. */
  private static readonly oSpecsByToken: Map<string, TPlaceholderSpec> =
    new Map<string, TPlaceholderSpec>(
      TIVO_PLUS_PLACEHOLDERS.map((spec) => [spec.token, spec])
    );

  /**
   * Removes placeholders that affect the URL's *structure* rather than its
   * ad parameters, so the result can be parsed as a URL.
   *
   * Runs before URL extraction, and is a no-op on any string that does not
   * contain the placeholder — so it is safe to apply to every tuning URI,
   * TiVo+ or not, without first classifying the channel.
   *
   * @param uri - A tuning URI or playback URL, templated or not.
   * @returns The input with structural placeholders removed.
   */
  public static resolveStructuralPlaceholders(uri: string): string {
    if (uri.indexOf(TIVOPLUS_BLANK) === -1) {
      return uri;
    }

    return uri.split(TIVOPLUS_BLANK).join("");
  }

  /**
   * Substitutes every `TIVOPLUS_*` placeholder in a playback URL.
   *
   * @param url - The templated playback URL.
   * @param values - Device, application and privacy values.
   * @param context - Per-channel values.
   * @returns The resolved URL, ready to play. A URL carrying no placeholder
   *   is returned unchanged, so this is safe to call unconditionally.
   */
  public static resolve(
    url: string,
    values: TTivoPlusValues,
    context: TTivoPlusContext
  ): string {
    if (url.indexOf("TIVOPLUS_") === -1) {
      return url;
    }

    const options = TivoPlusUrlResolver.locateXumoBlock(url);

    // A single pass. Resolving in one sweep means a substituted value that
    // happens to contain "TIVOPLUS_" is never re-resolved.
    return url.replaceAll(
      PLACEHOLDER_PATTERN,
      (token: string, offset: number): string =>
        TivoPlusUrlResolver.resolveToken(
          token,
          offset,
          values,
          context,
          options
        )
    );
  }

  /** Clears one-time logging. Used by tests and on playback teardown. */
  public static resetSessionLogState(): void {
    TivoPlusUrlResolver.oReportedUnknownTokens.clear();
  }

  /**
   * Locates the XUMO grouped block, whose payload is read by a separate
   * parser and therefore needs deeper encoding than the rest of the URL.
   */
  private static locateXumoBlock(url: string): TResolveOptions {
    const isXumo = url.indexOf(XUMO_MARKER_PARAM) !== -1;

    if (!isXumo) {
      return { isXumo: false, kvpsStart: -1, kvpsEnd: -1 };
    }

    const paramIndex = url.indexOf(XUMO_KVPS_PARAM);

    if (paramIndex === -1) {
      return { isXumo: true, kvpsStart: -1, kvpsEnd: -1 };
    }

    const kvpsStart = paramIndex + XUMO_KVPS_PARAM.length;
    const nextParam = url.indexOf("&", kvpsStart);

    return {
      isXumo: true,
      kvpsStart,
      kvpsEnd: nextParam === -1 ? url.length : nextParam
    };
  }

  /** Resolves one placeholder occurrence. */
  private static resolveToken(
    token: string,
    offset: number,
    values: TTivoPlusValues,
    context: TTivoPlusContext,
    options: TResolveOptions
  ): string {
    const spec = TivoPlusUrlResolver.oSpecsByToken.get(token);

    if (spec === undefined) {
      TivoPlusUrlResolver.reportUnknownTokenOnce(token);
      return "";
    }

    const value = spec.read(values, context);

    // Literal values are constants or already-constrained device facts, so
    // the reference substitutes them verbatim wherever they appear —
    // including inside the grouped block, which is why they are not encoded
    // here either.
    if (spec.kind === PlaceholderKind.Literal) {
      return value;
    }

    return TivoPlusUrlResolver.encodeIdentityValue(value, offset, options);
  }

  /**
   * Encodes an identity-bearing value for the position it occupies.
   *
   * Inside XUMO's grouped block the payload is encoded three times: the
   * block itself already arrives doubly encoded, and the value has to
   * survive both of those unwrappings plus its own. Outside the block,
   * characters that break XUMO's parser are stripped — deleted, not escaped
   * — before a single encode. Non-XUMO URLs get one encode and nothing else.
   */
  private static encodeIdentityValue(
    value: string,
    offset: number,
    options: TResolveOptions
  ): string {
    if (value === "") {
      return "";
    }

    const isInsideKvps =
      options.kvpsStart !== -1 &&
      offset >= options.kvpsStart &&
      offset < options.kvpsEnd;

    if (isInsideKvps) {
      return TivoPlusUrlResolver.encodeTimes(value, KVPS_ENCODE_DEPTH);
    }

    const sanitised = options.isXumo
      ? value.replaceAll(XUMO_UNSAFE_CHARACTERS, "")
      : value;

    return TivoPlusUrlResolver.encodeTimes(sanitised, DEFAULT_ENCODE_DEPTH);
  }

  /** Applies `encodeURIComponent` the given number of times. */
  private static encodeTimes(value: string, times: number): string {
    let encoded = value;

    for (let index = 0; index < times; index += 1) {
      encoded = encodeURIComponent(encoded);
    }

    return encoded;
  }

  /**
   * Reports an unrecognised placeholder once per session.
   *
   * Unknown tokens resolve to an empty string rather than being left in
   * place: a literal `TIVOPLUS_…` reaching a content partner is worse than
   * an empty parameter, and the log makes the gap visible so the catalogue
   * can be extended.
   */
  private static reportUnknownTokenOnce(token: string): void {
    if (TivoPlusUrlResolver.oReportedUnknownTokens.has(token)) {
      return;
    }

    TivoPlusUrlResolver.oReportedUnknownTokens.add(token);

    Log.e(
      TivoPlusUrlResolver.TAG,
      "unrecognised placeholder " + token + " resolved to an empty value"
    );
  }
}
